/**
 * Order (Pöntun) API Service
 * Handles order/collection request operations
 */

import type { Order, ApiResponse } from '../types';
import { fetchApi } from './api';

/**
 * Order API endpoints
 * Manages waste collection orders and requests
 */
export const orderApi = {
  /**
   * Get all orders with optional filters
   * @param params - Filter parameters (status, customer_id, type)
   * @returns Promise with orders array
   */
  async getAll(params?: {
    status?: string;
    customer_id?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Order[]>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    return fetchApi<ApiResponse<Order[]>>(`/api/pantanir${query ? `?${query}` : ''}`);
  },

  /**
   * Get order details by ID
   * @param id - Order ID
   * @returns Promise with order details
   */
  async getById(id: string): Promise<{ data: Order }> {
    return fetchApi(`/api/pantanir/${id}`);
  },

  /**
   * Create new order
   * @param order - Order data (partial, ID auto-generated)
   * @returns Promise with created order
   */
  async create(order: Partial<Order>): Promise<{ data: Order }> {
    return fetchApi('/api/pantanir', {
      method: 'POST',
      body: JSON.stringify(order),
    });
  },

  /**
   * Update order
   * @param id - Order ID
   * @param order - Updated order data (partial)
   * @returns Promise with updated order
   */
  async update(id: string, order: Partial<Order>): Promise<{ data: Order }> {
    return fetchApi(`/api/pantanir/${id}`, {
      method: 'PUT',
      body: JSON.stringify(order),
    });
  },

  /**
   * Delete order
   * @param id - Order ID
   * @returns Promise with success message
   */
  async delete(id: string): Promise<{ message: string }> {
    return fetchApi(`/api/pantanir/${id}`, {
      method: 'DELETE',
    });
  },
};
